import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { Badge, Button, Input, Switch, Textarea } from "../../ui";
import { FINANZAS_OPERATIVAS_BASE } from "../../../Layouts/finanzas-operativas/paths";
import type { QuoteWizard } from "../useQuoteWizard";
import { QuoteTotals, formatDuration } from "../QuoteTotals";
import { QuoteStatusBadge } from "../QuoteStatusBadge";

interface ResultStepProps {
  wizard: QuoteWizard;
}

/** Paso 3 — resumen, vigencia/notas/términos y acciones (guardar, PDF). */
export function ResultStep({ wizard }: ResultStepProps) {
  const { t } = useTranslation("finanzasOperativas");
  const navigate = useNavigate();
  const { estimate, result, setResult, saved, service } = wizard;
  if (!estimate) return null;

  const place = (p: { address?: string; lat?: number; lng?: number } | null | undefined) =>
    p?.address || (p?.lat != null && p?.lng != null ? `${p.lat.toFixed(4)}, ${p.lng.toFixed(4)}` : "—");

  return (
    <div className="space-y-3">
      {saved && (
        <div className="flex flex-wrap items-center gap-2 rounded-lg border border-success/30 bg-success/10 px-3 py-2 text-sm">
          <i className="ri-checkbox-circle-line text-success" />
          <span className="font-medium text-content">{t("quotes.result.saved")}</span>
          <span className="text-content-secondary">{t("quotes.result.savedFolio", { folio: saved.folio })}</span>
          <QuoteStatusBadge status={saved.status} size="sm" />
          <span className="flex-1" />
          <Button variant="link" size="xs" onClick={() => navigate(`${FINANZAS_OPERATIVAS_BASE}/cotizaciones?id=${saved.id}`)}>
            {t("quotes.result.viewInFinance")}
          </Button>
        </div>
      )}

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-5">
        <div className="space-y-2 sm:col-span-3">
          <h4 className="border-b border-line pb-2 text-sm font-semibold text-content">{t("quotes.result.summary")}</h4>
          <dl className="grid grid-cols-1 gap-x-4 gap-y-1.5 text-sm sm:grid-cols-2">
            <Row k={t("quotes.fields.customer")} v={service.customer_snapshot.name || "—"} />
            <Row k={t("quotes.fields.contact")} v={service.customer_snapshot.contact_name || service.customer_snapshot.email || "—"} />
            <Row k={t("quotes.fields.origin")} v={place(estimate.origin)} />
            <Row k={t("quotes.fields.destination")} v={place(estimate.destination)} />
            <Row k={t("quotes.fields.vehicleClass")} v={t(`costProfiles.vehicleClasses.${estimate.vehicle_class}`)} />
            <Row k={t("quotes.fields.unit")} v={estimate.unit_snapshot ? [estimate.unit_snapshot.name, estimate.unit_snapshot.plate].filter(Boolean).join(" · ") : "—"} />
            <Row k={t("quotes.fields.distance")} v={`${estimate.distance_km.toLocaleString("es-MX")} km`} />
            <Row k={t("quotes.fields.duration")} v={`${formatDuration(estimate.duration_min)} · ${estimate.days} d`} />
            <Row k={t("quotes.fields.tolls")} v={<Badge tone="neutral" variant="outline" size="sm">{t(`quotes.calculation.tollsSource.${estimate.tolls.source}`, { defaultValue: estimate.tolls.source })}</Badge>} />
          </dl>
        </div>
        <div className="sm:col-span-2">
          <QuoteTotals estimate={estimate} />
        </div>
      </div>

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <Input label={t("quotes.fields.title")} value={result.title} disabled={!!saved} onChange={(e) => setResult({ ...result, title: e.target.value })} />
        <Input label={t("quotes.fields.validUntil")} type="date" value={result.valid_until} disabled={!!saved} onChange={(e) => setResult({ ...result, valid_until: e.target.value })} />
        <Textarea containerClassName="sm:col-span-2" label={t("quotes.fields.notes")} rows={2} value={result.notes} disabled={!!saved} onChange={(e) => setResult({ ...result, notes: e.target.value })} />
        <Textarea containerClassName="sm:col-span-2" label={t("quotes.fields.terms")} rows={2} value={result.terms} disabled={!!saved} onChange={(e) => setResult({ ...result, terms: e.target.value })} />
        <div className="flex items-center gap-3 sm:col-span-2">
          <Switch checked={result.show_breakdown} onChange={(v) => setResult({ ...result, show_breakdown: v })} aria-label={t("quotes.fields.showBreakdown")} />
          <div>
            <p className="text-sm font-medium text-content">{t("quotes.fields.showBreakdown")}</p>
            <p className="text-xs text-content-faint">{t("quotes.result.breakdownHint")}</p>
          </div>
        </div>
      </div>

      {saved?.pdf?.url && (
        <div className="flex flex-wrap items-center gap-2 rounded-lg border border-line px-3 py-2 text-sm">
          <i className="ri-file-pdf-2-line text-error" />
          <span className="text-content">{saved.pdf.filename ?? `${saved.folio}.pdf`}</span>
          <span className="flex-1" />
          <Button variant="outline" size="sm" onClick={() => window.open(saved.pdf!.url, "_blank", "noopener")} icon={<i className="ri-external-link-line" />}>
            {t("quotes.result.openPdf")}
          </Button>
          <a href={saved.pdf.url} download={saved.pdf.filename ?? `${saved.folio}.pdf`} className="inline-flex">
            <Button variant="soft" size="sm" icon={<i className="ri-download-2-line" />}>
              {t("quotes.result.download")}
            </Button>
          </a>
        </div>
      )}
      {!saved && <p className="text-xs text-content-faint">{t("quotes.result.saveFirst")}</p>}
    </div>
  );
}

function Row({ k, v }: { k: string; v: React.ReactNode }) {
  return (
    <div className="flex flex-col">
      <dt className="text-xs text-content-faint">{k}</dt>
      <dd className="text-content">{v}</dd>
    </div>
  );
}
